import { type CanvasElementType } from '@blaze-runtime/engine-domain';
import { type ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { type SelectOption } from '@blaze-shared/shared-util';

export type GenerationElementType = CanvasElementType | 'dateInput' | 'timeInput';

export type GeneratorInputOption = SelectOption<GenerationElementType>;

export const barcodeOption: GeneratorInputOption = {
  value: 'barcodeScanner',
  label: 'Barcode Scanner',
};
export const checkboxGroupOption: GeneratorInputOption = {
  value: 'checkbox',
  label: 'Checkbox Group',
};
export const checkboxInputOption: GeneratorInputOption = {
  value: 'checkboxInput',
  label: 'Checkbox Input',
};
export const dateOption: GeneratorInputOption = { value: 'dateInput', label: 'Date' };
export const datetimeOption: GeneratorInputOption = { value: 'datetimeInput', label: 'Date Time' };
export const dropdownOption: GeneratorInputOption = { value: 'dropdown', label: 'Dropdown' };
export const iframeOption: GeneratorInputOption = { value: 'iframe', label: 'IFrame' };
export const imageOption: GeneratorInputOption = { value: 'image', label: 'Image' };
export const videoOption: GeneratorInputOption = { value: 'videoPlayer', label: 'Video' };
export const pdfViewerOption: GeneratorInputOption = { value: 'pdfViewer', label: 'PDF Viewer' };
export const qrOption: GeneratorInputOption = { value: 'qrcode', label: 'QR Code' };
export const radioOption: GeneratorInputOption = { value: 'radio', label: 'Radio' };
export const scoreOption: GeneratorInputOption = { value: 'score', label: 'Score' };
export const signatureOption: GeneratorInputOption = { value: 'signature', label: 'Signature' };
export const sliderOption: GeneratorInputOption = { value: 'slider', label: 'Slider' };
export const tagsOption: GeneratorInputOption = { value: 'tags', label: 'Tags' };
export const textFieldOption: GeneratorInputOption = {
  value: 'textField',
  label: 'Text Field (read-only)',
};
export const textInputOption: GeneratorInputOption = { value: 'textInput', label: 'Text Input' };
export const timeOption: GeneratorInputOption = { value: 'timeInput', label: 'Time' };
export const toggleButtonOption: GeneratorInputOption = {
  value: 'toggleButton',
  label: 'Toggle Button',
};
export const audioRecorderOption: GeneratorInputOption = {
  value: 'audioRecorder',
  label: 'Record',
};
export const uploadFileOption: GeneratorInputOption = {
  value: 'uploadFile',
  label: 'File Uploader',
};

export const formFieldOptions: GeneratorInputOption[] = [
  audioRecorderOption,
  barcodeOption,
  checkboxGroupOption,
  checkboxInputOption,
  dateOption,
  datetimeOption,
  dropdownOption,
  iframeOption,
  imageOption,
  pdfViewerOption,
  qrOption,
  radioOption,
  scoreOption,
  signatureOption,
  sliderOption,
  tagsOption,
  textFieldOption,
  textInputOption,
  timeOption,
  toggleButtonOption,
  uploadFileOption,
];

// maps datatype to available [0 - editable, 1 - readonly] elements
const FORM_FIELDS_DATA_TYPE_MAPPING: Record<
  0 | 1,
  Record<ColumnDataType, GeneratorInputOption[]>
> = {
  0: {
    Array: [checkboxGroupOption, tagsOption],
    AutoNumber: [],
    Boolean: [checkboxInputOption, radioOption, toggleButtonOption],
    CreatedBy: [],
    CreatedTime: [],
    Date: [dateOption, datetimeOption, textFieldOption],
    DateTime: [datetimeOption, timeOption, textFieldOption],
    Duration: [textInputOption],
    File: [uploadFileOption, audioRecorderOption],
    Formula: [],
    GeoPoint: [textInputOption],
    Image: [imageOption, textInputOption],
    LastUpdatedTime: [],
    Link: [dropdownOption, tagsOption, radioOption, checkboxGroupOption, textFieldOption],
    Number: [textInputOption, dropdownOption, sliderOption, scoreOption],
    Object: [],
    Signature: [signatureOption],
    String: [
      textInputOption,
      textFieldOption,
      dropdownOption,
      radioOption,
      imageOption,
      qrOption,
      audioRecorderOption,
      uploadFileOption,
      barcodeOption,
      iframeOption,
    ],
    Tag: [tagsOption, radioOption, checkboxGroupOption, textFieldOption, dropdownOption],
    Time: [timeOption, datetimeOption],
    UpdatedBy: [],
    Rollup: [textInputOption],
  },
  1: {
    Array: [checkboxGroupOption, tagsOption, textFieldOption],
    AutoNumber: [],
    Boolean: [checkboxInputOption, radioOption],
    CreatedBy: [],
    CreatedTime: [],
    Date: [textFieldOption],
    DateTime: [textFieldOption],
    Duration: [textFieldOption],
    File: [uploadFileOption, pdfViewerOption, audioRecorderOption],
    Formula: [],
    GeoPoint: [textFieldOption],
    Image: [imageOption, textFieldOption],
    LastUpdatedTime: [],
    Link: [textFieldOption, tagsOption, radioOption, checkboxGroupOption],
    Number: [textFieldOption],
    Object: [],
    Rollup: [textFieldOption],
    Signature: [signatureOption],
    String: [textFieldOption, pdfViewerOption, qrOption, imageOption, videoOption, iframeOption],
    Tag: [tagsOption, radioOption, checkboxGroupOption, textFieldOption],
    Time: [textFieldOption],
    UpdatedBy: [],
  },
};
export function getFormFieldDataTypeMapping(
  isFormReadonly: boolean,
  columnDataType: ColumnDataType,
): GeneratorInputOption[] {
  return FORM_FIELDS_DATA_TYPE_MAPPING[isFormReadonly ? 1 : 0][columnDataType];
}
export function getFormFieldsDataTypeMapping(
  isFormReadonly: boolean,
): Record<ColumnDataType, GeneratorInputOption[]> {
  return FORM_FIELDS_DATA_TYPE_MAPPING[isFormReadonly ? 1 : 0];
}
